import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminAPI } from '../../api/client';

const LIMIT = 20;

const STATUS_OPTIONS = [
  { value: '', label: '전체' },
  { value: 'success', label: '성공' },
  { value: 'failed', label: '실패' },
];

export default function AdminLogs() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-logs', page, status, search],
    queryFn: async () => {
      const result = await adminAPI.getLogs({ page, limit: LIMIT, status, search });
      if (!result.success) throw new Error(result.error || '로그를 불러올 수 없습니다');
      return result;
    },
  });

  const logs = data?.data || data?.logs || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleStatusChange = (val) => {
    setStatus(val);
    setPage(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('ko-KR', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-charcoal">예약 로그</h2>
        <p className="text-sm text-sub-text mt-1">전체 예약 실행 기록</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Status segment */}
        <div className="flex rounded-xl border border-border overflow-hidden bg-white">
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => handleStatusChange(opt.value)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                status === opt.value
                  ? 'bg-tayo text-white'
                  : 'text-sub-text hover:bg-snow'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-2 flex-1">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="학번 또는 이름으로 검색"
            className="flex-1 px-4 py-2 rounded-xl border border-border text-sm text-charcoal placeholder:text-sub-text focus:outline-none focus:ring-2 focus:ring-tayo/30 bg-white"
          />
          <button
            type="submit"
            className="px-5 py-2 rounded-xl bg-tayo text-white text-sm font-medium hover:bg-tayo-dark transition-colors"
          >
            검색
          </button>
          {search && (
            <button
              type="button"
              onClick={() => { setSearchInput(''); setSearch(''); setPage(1); }}
              className="px-4 py-2 rounded-xl bg-gray-100 text-charcoal text-sm font-medium hover:bg-gray-200 transition-colors"
            >
              초기화
            </button>
          )}
        </form>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-coral/10 border border-coral/20 rounded-xl p-4">
          <p className="text-sm text-coral">{error.message}</p>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-tayo" />
          <p className="mt-2 text-sub-text">로딩 중...</p>
        </div>
      )}

      {/* Table */}
      {!isLoading && (
        <div className="bg-white rounded-2xl shadow-card overflow-hidden">
          {logs.length === 0 ? (
            <div className="py-16 text-center">
              <p className="text-sub-text">데이터가 없습니다</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-border">
                  <thead className="bg-snow">
                    <tr>
                      {['실행 시각', '사용자', '노선', '시간', '좌석', '상태', '소요시간', '에러'].map((h) => (
                        <th key={h} className="px-5 py-3 text-left text-xs font-medium text-charcoal uppercase tracking-wider whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-border">
                    {logs.map((log) => (
                      <tr key={log.id} className="hover:bg-snow transition-colors">
                        <td className="px-5 py-3 whitespace-nowrap text-sm text-charcoal">
                          {formatDate(log.executed_at)}
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap text-sm text-charcoal">
                          <div className="font-medium">{log.user?.name || log.user?.student_id || '-'}</div>
                          {log.user?.student_id && log.user?.name && (
                            <div className="text-xs text-sub-text">{log.user.student_id}</div>
                          )}
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap text-sm text-charcoal">
                          {log.route_name || '-'}
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap text-sm text-charcoal">
                          {log.bus_time || '-'}
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap text-sm text-charcoal">
                          {log.seat_no ? `${log.seat_no}번` : '-'}
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap">
                          <span className={`inline-flex text-xs font-semibold rounded-full px-2.5 py-0.5 ${
                            log.status === 'success'
                              ? 'bg-emerald-50 text-mint'
                              : 'bg-coral/10 text-coral'
                          }`}>
                            {log.status === 'success' ? '성공' : '실패'}
                          </span>
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap text-sm text-sub-text">
                          {log.metadata?.open_to_complete_ms
                            ? `${log.metadata.open_to_complete_ms}ms`
                            : log.execution_time_ms ? `${log.execution_time_ms}ms` : '-'}
                        </td>
                        <td className="px-5 py-3 text-sm text-sub-text max-w-xs truncate">
                          {log.error_message || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="bg-white px-5 py-3 border-t border-border flex items-center justify-between">
                <p className="text-sm text-sub-text">
                  <span className="font-medium text-charcoal">{total}</span>건 중{' '}
                  <span className="font-medium text-charcoal">{(page - 1) * LIMIT + 1}</span>
                  {' - '}
                  <span className="font-medium text-charcoal">{Math.min(page * LIMIT, total)}</span>
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 text-sm font-medium text-charcoal bg-gray-100 rounded-full hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    이전
                  </button>
                  <span className="text-sm text-sub-text">{page} / {totalPages}</span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-4 py-2 text-sm font-medium text-white bg-tayo rounded-full hover:bg-tayo-dark disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    다음
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
